import React from 'react';

import { css } from '@emotion/react';
import { NeutralColorToken, TextButton, Type } from '@robinhood/design';

import { AddressInput } from 'src/clients/Network/gql/types/address';
import AddressAutocompleteInput from 'src/components/creditApplication/AddressAutocompleteInput';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';

interface IPropsIface {
  addressType: 'residential' | 'shipping';
  onEnterManually: () => void;
  onContinue: (address: AddressInput) => void;
  loading?: boolean;
}

const AddressSearchPage = ({ addressType, onEnterManually, onContinue, loading = false }: IPropsIface) => {
  const [selectedAddress, setSelectedAddress] = React.useState<AddressInput>();
  const title = React.useMemo(() => {
    return addressType === 'residential' ? 'Enter your residential address' : 'Your shipping address';
  }, [addressType]);
  const placeholder = React.useMemo(() => {
    return addressType === 'residential' ? 'Your residential address' : 'Your shipping address';
  }, [addressType]);
  const buttonState = React.useMemo(() => {
    return loading ? 'loading' : selectedAddress == null ? 'disabled' : 'default';
  }, [loading, selectedAddress]);

  const handleAddressSelect = React.useCallback((address?: AddressInput) => {
    setSelectedAddress(address);
  }, []);

  const handleContinue = React.useCallback(() => {
    if (selectedAddress == null) {
      return;
    }
    onContinue(selectedAddress);
  }, [onContinue, selectedAddress]);

  return (
    <CreditApplicationPageLayout title={title}>
      <div css={root}>
        <AddressAutocompleteInput
          placeholder={placeholder}
          onSelect={handleAddressSelect}
          autoCompleteTypes={['street_address', 'premise']}
          regionCodes={['US', 'CA']}
        />
        <div css={textContainer}>
          <Type.Text size='M'>Can't find your address?</Type.Text>
          <TextButton
            label='Enter it manually.'
            type='button'
            onClick={onEnterManually}
            color={NeutralColorToken.Fg1}
          />
        </div>
        <Button label='Continue' type='button' width='full' onClick={handleContinue} state={buttonState} />
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 40px;
`;

const textContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export default AddressSearchPage;
